import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import AlertsPanel from '../components/AlertsPanel';
import StatsCards from '../components/StatsCards';
import { hasAnyPermission } from '../lib/permissions';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';
import { 
  Menu, 
  Home, 
  MapPin, 
  Building2, 
  Settings, 
  Pill, 
  UtensilsCrossed, 
  FileText, 
  Search, 
  LogOut, 
  User, 
  ChevronRight,
  ChevronLeft,
  PanelLeftClose,
  PanelLeft,
  LayoutDashboard,
  PawPrint,
  Map,
  Hash,
  Plus,
  Download,
  Eye,
  ScanLine,
  ShieldCheck,
  LifeBuoy,
  Sparkles,
  BookOpen,
  BellRing
} from 'lucide-react';

// Menu items configuration
const menuItems = {
  dashboards: [
    { id: 'home', label: 'Dashboard', icon: LayoutDashboard, path: '/', color: 'text-emerald-600', bgHover: 'hover:bg-emerald-50', permissions: ['dashboards.view'] },
    { id: 'medications', label: 'Medicações', icon: Pill, path: '/medications', color: 'text-orange-600', bgHover: 'hover:bg-orange-50', permissions: ['medication.dashboard.view'] },
    { id: 'feeding', label: 'Alimentação', icon: UtensilsCrossed, path: '/feeding', color: 'text-amber-600', bgHover: 'hover:bg-amber-50', permissions: ['feeding.dashboard.view'] },
    { id: 'cleaning', label: 'Limpeza', icon: Sparkles, path: '/cleaning', color: 'text-teal-700', bgHover: 'hover:bg-teal-50', permissions: ['cleaning.view'] },
  ],
  navigation: [
    { id: 'animals', label: 'Buscar Animais', icon: Search, path: '/animals', color: 'text-emerald-700', bgHover: 'hover:bg-emerald-50', permissions: ['animals.view'] },
    { id: 'map', label: 'Mapa de Bases', icon: Map, path: '/bases/map', color: 'text-teal-600', bgHover: 'hover:bg-teal-50', permissions: ['animals.view'] },
    { id: 'qr-scanner', label: 'Leitor QR', icon: ScanLine, path: '/qr-scanner', color: 'text-slate-700', bgHover: 'hover:bg-slate-50', permissions: ['qr.use'] },
  ],
  admin: [
    { id: 'users', label: 'Usuários', icon: User, path: '/users', color: 'text-slate-700', bgHover: 'hover:bg-slate-50', permissions: ['users.view'] },
    { id: 'bases', label: 'Gerenciar Bases', icon: Home, path: '/bases', color: 'text-emerald-700', bgHover: 'hover:bg-emerald-50', permissions: ['bases.manage'] },
    { id: 'organizations', label: 'Órgãos', icon: Building2, path: '/organizations', color: 'text-purple-600', bgHover: 'hover:bg-purple-50', permissions: ['organizations.manage'] },
    { id: 'species', label: 'Espécies', icon: BookOpen, path: '/settings/species', color: 'text-lime-700', bgHover: 'hover:bg-lime-50', permissions: ['species.manage'] },
    { id: 'sequence', label: 'Sequência ID', icon: Hash, path: '/settings/sequence', color: 'text-indigo-600', bgHover: 'hover:bg-indigo-50', permissions: ['sequence.manage'] },
    { id: 'reports', label: 'Relatórios', icon: FileText, path: '/reports', color: 'text-cyan-600', bgHover: 'hover:bg-cyan-50', permissions: ['reports.view'] },
    { id: 'lab', label: 'Laboratório', icon: Building2, path: '/lab-requests', color: 'text-rose-600', bgHover: 'hover:bg-rose-50', permissions: ['lab_requests.view'] },
    { id: 'support', label: 'Suporte', icon: LifeBuoy, path: '/support', color: 'text-blue-700', bgHover: 'hover:bg-blue-50', permissions: ['support_tickets.view'] },
    { id: 'audit', label: 'Auditoria', icon: ShieldCheck, path: '/audit', color: 'text-slate-900', bgHover: 'hover:bg-slate-50', roles: ['programador'] },
    { id: 'notices', label: 'Notificações', icon: BellRing, path: '/notices', color: 'text-emerald-700', bgHover: 'hover:bg-emerald-50', roles: ['programador'] },
  ]
};

const isGlobalSupportDashboardUser = (user) => {
  return user?.role === 'programador'
    || user?.role === 'administrador'
    || `${user?.name || ''} ${user?.full_name || ''} ${user?.email || ''}`.toLowerCase().includes('paula');
};

const buildSupportSummary = (tickets = []) => {
  const openTickets = tickets.filter((ticket) => !['concluido', 'cancelado'].includes(ticket.status));
  const waitingFirstResponse = openTickets.filter((ticket) => !ticket.first_response_at);
  const now = Date.now();
  return {
    total: tickets.length,
    open: openTickets.length,
    completed: tickets.filter((ticket) => ticket.status === 'concluido').length,
    waiting_first_response: waitingFirstResponse.length,
    overdue_first_response: waitingFirstResponse.filter((ticket) => ticket.first_response_due_at && new Date(ticket.first_response_due_at).getTime() < now).length,
    overdue_delivery: openTickets.filter((ticket) => ticket.delivery_due_at && new Date(ticket.delivery_due_at).getTime() < now).length,
    next_deliveries: openTickets
      .filter((ticket) => ticket.delivery_due_at)
      .sort((a, b) => new Date(a.delivery_due_at) - new Date(b.delivery_due_at))
      .slice(0, 6),
  };
};

const getRequestErrorMessage = (error, fallback) => {
  const status = error.response?.status;
  const detail = error.response?.data?.detail;
  if (status && detail) return `${fallback}: ${status} - ${detail}`;
  if (status) return `${fallback}: erro ${status}`;
  return fallback;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [admissions, setAdmissions] = useState([]);
  const [bases, setBases] = useState([]);
  const [stats, setStats] = useState(null);
  const [supportSummary, setSupportSummary] = useState(null);
  const [supportSummaryError, setSupportSummaryError] = useState('');
  const [cleaningSummary, setCleaningSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Filters
  const [filterBase, setFilterBase] = useState('all');
  const [filterCity, setFilterCity] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');

  useEffect(() => {
    fetchBases();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchSupportSummary();
    fetchCleaningSummary();
  }, [user?.id, user?.role]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSupportSummary = async () => {
    if (!hasAnyPermission(user, ['support_tickets.view'])) return;
    try {
      setSupportSummaryError('');
      const response = await axios.get(`${API}/support-tickets/dashboard`);
      setSupportSummary(response.data);
    } catch (error) {
      console.error('Error fetching support summary:', error);
      try {
        const fallback = await axios.get(`${API}/support-tickets`);
        setSupportSummary(buildSupportSummary(fallback.data || []));
      } catch (fallbackError) {
        console.error('Error fetching support fallback:', fallbackError);
        setSupportSummaryError(getRequestErrorMessage(fallbackError, 'Erro ao carregar resumo de suporte'));
      }
    }
  };

  const fetchCleaningSummary = async () => {
    if (!hasAnyPermission(user, ['cleaning.view'])) return;
    try {
      const response = await axios.get(`${API}/cleaning-dashboard`);
      setCleaningSummary(response.data.summary || null);
    } catch (error) {
      console.error('Error fetching cleaning summary:', error);
    }
  };

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchAdmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterBase && filterBase !== 'all') params.append('base_id', filterBase);
      if (filterCity) params.append('city', filterCity);
      if (filterGroup && filterGroup !== 'all') params.append('animal_group', filterGroup);
      
      const response = await axios.get(`${API}/admissions/summary?${params.toString()}`);
      setAdmissions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar admissões');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmissions();
  }, [filterBase, filterCity, filterGroup]);

  const handleDownloadPDF = async (id, ibamaId) => {
    try {
      const response = await axios.get(`${API}/admissions/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `admissao_${ibamaId.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const getGroupBadgeColor = (group) => {
    switch (group) {
      case 'AVE': return 'bg-sky-500';
      case 'MAM': return 'bg-amber-500';
      case 'REPT': return 'bg-green-600';
      default: return 'bg-gray-500';
    }
  };

  const isActivePath = (path) => location.pathname === path;
  const canShowMenuItem = (item) => {
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (item.id === 'support' && user?.role === 'programador') return true;
    if (item.permissions && !hasAnyPermission(user, item.permissions)) return false;
    return true;
  };
  const hasAdminMenu = menuItems.admin.some(canShowMenuItem);
  const isActiveAdmission = (admission) => (
    ['Internado', 'Em tratamento'].includes(admission.status)
    && admission.is_active !== false
    && !admission.outcome_date
  );
  const activeAdmissions = admissions.filter(isActiveAdmission);
  const releasedStatuses = ['Alta', 'Soltura', 'Alta Clínica', 'released', 'Liberado'];
  const deathStatuses = ['Óbito', 'Obito', 'Eutanásia', 'Eutanasia', 'death'];
  const derivedByLocation = activeAdmissions.reduce((acc, admission) => {
    const location = (admission.allocation_location || '').toLowerCase();
    if (location.includes('uti')) {
      acc.uti += 1;
    } else if (location.includes('quarentena')) {
      acc.quarentena += 1;
    } else if (location.includes('internação') || location.includes('internacao')) {
      acc.internacao += 1;
    } else {
      acc.outros += 1;
    }
    return acc;
  }, { internacao: 0, uti: 0, quarentena: 0, outros: 0 });
  const dashboardStats = stats ? {
    ...stats,
    inTreatment: activeAdmissions.length,
    released: admissions.filter((admission) => releasedStatuses.includes(admission.status)).length,
    deaths: admissions.filter((admission) => deathStatuses.includes(admission.status)).length,
    byLocation: derivedByLocation,
  } : null;
  const canViewSupportDashboard = user?.role === 'programador' || hasAnyPermission(user, ['support_tickets.view']);
  const canViewGlobalSupportDashboard = isGlobalSupportDashboardUser(user);
  const canViewCleaningDashboard = hasAnyPermission(user, ['cleaning.view']);
  const canCreateAdmission = hasAnyPermission(user, ['animals.edit_admin_data']);
  const supportDashboardData = supportSummary || {
    total: 0,
    open: 0,
    completed: 0,
    waiting_first_response: 0,
    overdue_first_response: 0,
    overdue_delivery: 0,
    next_deliveries: [],
  };

  const renderMenuItem = (item, collapsed) => {
    if (!canShowMenuItem(item)) return null;
    
    const isActive = isActivePath(item.path);
    const Icon = item.icon;
    
    if (collapsed) {
      return (
        <TooltipProvider key={item.id} delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate(item.path)}
                className={`w-full p-3 rounded-lg flex items-center justify-center transition-all ${
                  isActive 
                    ? `${item.color} bg-white shadow-sm` 
                    : `text-gray-600 ${item.bgHover}`
                }`}
              >
                <Icon className="w-5 h-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{item.label}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    
    return (
      <button
        key={item.id}
        onClick={() => navigate(item.path)}
        className={`w-full px-3 py-2.5 rounded-lg flex items-center gap-3 transition-all ${
          isActive 
            ? `${item.color} bg-white shadow-sm font-medium` 
            : `text-gray-600 ${item.bgHover}`
        }`}
      >
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="truncate">{item.label}</span>
      </button>
    );
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      toast.error('Preencha todos os campos de senha');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('A nova senha e a confirmação não conferem');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/users/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmNewPassword,
      });
      toast.success('Senha alterada com sucesso');
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 flex">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-white border-r border-emerald-100 transition-all duration-300 ${
        sidebarCollapsed ? 'w-[70px]' : 'w-[240px]'
      }`}>
        {/* Sidebar Header */}
        <div className={`p-4 border-b border-emerald-100 flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div>
                <h1 className="text-sm font-bold text-emerald-800" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Sistema CRAS
                </h1>
                {SHOW_INSTITUTO_BW_BRANDING && (
                  <p className="text-[10px] text-emerald-500">Instituto BW</p>
                )}
              </div>
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="BW" className="h-10 w-10 object-contain" />
              )}
            </div>
          )}
          {sidebarCollapsed && SHOW_INSTITUTO_BW_BRANDING && (
            <img src="/bw-symbol-final.png" alt="BW" className="h-10 w-10 object-contain" />
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-6 overflow-y-auto">
          {/* Dashboards */}
          <div>
            {!sidebarCollapsed && (
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                Dashboards
              </p>
            )}
            <div className="space-y-1">
              {menuItems.dashboards.filter(canShowMenuItem).map(item => renderMenuItem(item, sidebarCollapsed))}
            </div>
          </div>

          {/* Navigation */}
          <div>
            {!sidebarCollapsed && (
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                Navegação
              </p>
            )}
            <div className="space-y-1">
              {menuItems.navigation.map(item => renderMenuItem(item, sidebarCollapsed))}
            </div>
          </div>

          {/* Admin */}
          {hasAdminMenu && (
            <div>
              {!sidebarCollapsed && (
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
                  Administração
                </p>
              )}
              <div className="space-y-1">
                {menuItems.admin.map(item => renderMenuItem(item, sidebarCollapsed))}
              </div>
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-3 border-t border-emerald-100">
          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full p-2 rounded-lg flex items-center justify-center gap-2 text-gray-500 hover:bg-gray-100 transition-all mb-2"
            data-testid="toggle-sidebar"
          >
            {sidebarCollapsed ? <PanelLeft className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
            {!sidebarCollapsed && <span className="text-sm">Recolher</span>}
          </button>
          
          {/* User Info */}
          {!sidebarCollapsed ? (
            <div className="p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium">
                  {user?.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-emerald-800 truncate">{user?.name}</p>
                  <p className="text-xs text-emerald-600 capitalize">{user?.role}</p>
                </div>
              </div>
              <Button
                onClick={() => setShowPasswordDialog(true)}
                variant="outline"
                size="sm"
                className="w-full mt-2"
              >
                Alterar senha
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-red-600 hover:bg-red-50"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          ) : (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={logout}
                    className="w-full p-3 rounded-lg flex items-center justify-center text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Sair ({user?.name})</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white shadow-sm border-b border-emerald-100 sticky top-0 z-50">
          <div className="px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h1 className="text-sm font-bold text-emerald-800">Sistema CRAS</h1>
                {SHOW_INSTITUTO_BW_BRANDING && (
                  <p className="text-[10px] text-emerald-500">Instituto BW</p>
                )}
              </div>
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img src="/bw-symbol-final.png" alt="BW" className="h-10 w-10 object-contain" />
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <div className="text-right mr-1">
                <p className="text-xs font-medium text-emerald-800">{user?.name?.split(' ')[0]}</p>
                <p className="text-[10px] text-emerald-600 capitalize">{user?.role}</p>
              </div>
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="border-emerald-300 h-9 w-9">
                    <Menu className="h-5 w-5 text-emerald-700" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0">
                  <SheetHeader className="p-4 border-b">
                    <SheetTitle className="text-emerald-800 flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white font-medium">
                        {user?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-left">{user?.name}</p>
                        <p className="text-sm font-normal text-emerald-600 capitalize">{user?.role}</p>
                      </div>
                    </SheetTitle>
                  </SheetHeader>
                  
                  <nav className="p-4 space-y-6">
                    {/* Dashboards */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Dashboards
                      </p>
                      <div className="space-y-1">
                        {menuItems.dashboards.filter(canShowMenuItem).map(item => (
                          <button
                            key={item.id}
                            onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                            className={`w-full flex items-center justify-between px-3 py-3 rounded-lg ${item.bgHover} ${item.color}`}
                          >
                            <span className="flex items-center gap-3">
                              <item.icon className="w-5 h-5" />
                              {item.label}
                            </span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Navigation */}
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Navegação
                      </p>
                      <div className="space-y-1">
                        {menuItems.navigation.map(item => (
                          <button
                            key={item.id}
                            onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                            className={`w-full flex items-center justify-between px-3 py-3 rounded-lg ${item.bgHover} ${item.color}`}
                          >
                            <span className="flex items-center gap-3">
                              <item.icon className="w-5 h-5" />
                              {item.label}
                            </span>
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Admin */}
          {hasAdminMenu && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                          Administração
                        </p>
                        <div className="space-y-1">
                          {menuItems.admin.filter(canShowMenuItem).map(item => (
                            <button
                              key={item.id}
                              onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                              className={`w-full flex items-center justify-between px-3 py-3 rounded-lg ${item.bgHover} ${item.color}`}
                            >
                              <span className="flex items-center gap-3">
                                <item.icon className="w-5 h-5" />
                                {item.label}
                              </span>
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="border-t pt-4">
                      <button
                        onClick={() => { setShowPasswordDialog(true); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-emerald-50 text-emerald-700"
                      >
                        <Settings className="w-5 h-5" />
                        Alterar senha
                      </button>
                      <button
                        onClick={() => { logout(); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-red-50 text-red-600"
                      >
                        <LogOut className="w-5 h-5" />
                        Sair do Sistema
                      </button>
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Page Title - Desktop Only */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-emerald-800">Dashboard</h1>
                <p className="text-sm text-gray-600">Visão geral do sistema CRAS</p>
              </div>
              {canCreateAdmission && (
                <Button
                  onClick={() => navigate('/admissions/new')}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="new-admission-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Admissão
                </Button>
              )}
            </div>

            {/* Stats Cards */}
            <div className="mb-6">
              <StatsCards stats={dashboardStats} />
            </div>

            {/* Alerts Panel */}
            <div className="mb-6">
              <AlertsPanel bases={bases} />
            </div>

            {canViewCleaningDashboard && (
              <Card className="mb-6 border-teal-100 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between gap-3 text-lg text-slate-900">
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-5 w-5 text-teal-700" />
                      Limpeza de Recintos
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigate('/cleaning')}>
                      Abrir limpeza
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    <div className="rounded-md bg-amber-50 p-3">
                      <p className="text-xs text-amber-700">Pendentes hoje</p>
                      <p className="text-xl font-semibold text-amber-800">{cleaningSummary?.pending || 0}</p>
                    </div>
                    <div className="rounded-md bg-red-50 p-3">
                      <p className="text-xs text-red-700">Atrasadas</p>
                      <p className="text-xl font-semibold text-red-700">{cleaningSummary?.overdue || 0}</p>
                    </div>
                    <div className="rounded-md bg-emerald-50 p-3">
                      <p className="text-xs text-emerald-700">Concluídas</p>
                      <p className="text-xl font-semibold text-emerald-800">{cleaningSummary?.completed || 0}</p>
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Sem limpeza prevista</p>
                      <p className="text-xl font-semibold text-slate-900">{Math.max((cleaningSummary?.scheduled || 0) - (cleaningSummary?.completed || 0), 0)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {canViewSupportDashboard && (
              <Card className="mb-6 border-blue-100 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between gap-3 text-lg text-slate-900">
                    <span className="flex items-center gap-2">
                      <LifeBuoy className="h-5 w-5 text-blue-700" />
                      {canViewGlobalSupportDashboard ? 'Dashboard de Suporte' : 'Minhas Solicitações de Suporte'}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigate('/support')}>
                      Abrir suporte
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
                    <div className="rounded-md bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Total</p>
                      <p className="text-xl font-semibold text-slate-900">{supportDashboardData.total}</p>
                    </div>
                    <div className="rounded-md bg-blue-50 p-3">
                      <p className="text-xs text-blue-700">Abertos</p>
                      <p className="text-xl font-semibold text-blue-800">{supportDashboardData.open}</p>
                    </div>
                    <div className="rounded-md bg-emerald-50 p-3">
                      <p className="text-xs text-emerald-700">Concluídos</p>
                      <p className="text-xl font-semibold text-emerald-800">{supportDashboardData.completed}</p>
                    </div>
                    <div className="rounded-md bg-amber-50 p-3">
                      <p className="text-xs text-amber-700">Sem 1ª resposta</p>
                      <p className="text-xl font-semibold text-amber-800">{supportDashboardData.waiting_first_response}</p>
                    </div>
                    <div className="rounded-md bg-red-50 p-3">
                      <p className="text-xs text-red-700">Resposta atrasada</p>
                      <p className="text-xl font-semibold text-red-700">{supportDashboardData.overdue_first_response}</p>
                    </div>
                    <div className="rounded-md bg-red-50 p-3">
                      <p className="text-xs text-red-700">Entrega atrasada</p>
                      <p className="text-xl font-semibold text-red-700">{supportDashboardData.overdue_delivery}</p>
                    </div>
                  </div>
                  {!supportSummary && (
                    <p className="mt-3 text-sm text-slate-500">
                      {supportSummaryError || 'Resumo carregando ou aguardando atualização do backend. Use o botão Abrir suporte para acessar os chamados.'}
                    </p>
                  )}
                  {supportDashboardData.next_deliveries?.length > 0 && (
                    <div className="mt-4 border-t pt-3">
                      <p className="mb-2 text-sm font-medium text-slate-700">Próximas entregas</p>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {supportDashboardData.next_deliveries.map((ticket) => (
                          <button key={ticket.id} type="button" onClick={() => navigate('/support')} className="rounded border border-slate-200 p-2 text-left text-sm hover:bg-slate-50">
                            <p className="font-medium text-slate-800">{ticket.title}</p>
                            <p className="text-xs text-slate-500">{ticket.delivery_due_at ? new Date(ticket.delivery_due_at).toLocaleString('pt-BR') : 'Sem prazo'} • {ticket.status}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Mobile: New Admission Button */}
            {canCreateAdmission && (
              <div className="lg:hidden mb-4">
                <Button
                  onClick={() => navigate('/admissions/new')}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                  data-testid="new-admission-button-mobile"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Nova Admissão
                </Button>
              </div>
            )}

            {/* Filters */}
            <Card className="mb-6 shadow-sm border-emerald-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-emerald-800 text-lg">Filtros</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-sm">Base</Label>
                    <Select value={filterBase} onValueChange={setFilterBase}>
                      <SelectTrigger data-testid="filter-base-select">
                        <SelectValue placeholder="Todas" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {bases.map((base) => (
                          <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Cidade</Label>
                    <Input
                      placeholder="Filtrar por cidade"
                      value={filterCity}
                      onChange={(e) => setFilterCity(e.target.value)}
                      data-testid="filter-city-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Grupo Animal</Label>
                    <Select value={filterGroup} onValueChange={setFilterGroup}>
                      <SelectTrigger data-testid="filter-group-select">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="AVE">Aves</SelectItem>
                        <SelectItem value="MAM">Mamíferos</SelectItem>
                        <SelectItem value="REPT">Répteis</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button
                      variant="outline"
                      onClick={() => { setFilterBase('all'); setFilterCity(''); setFilterGroup('all'); }}
                      className="w-full"
                    >
                      Limpar Filtros
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Animals Table */}
            <Card className="shadow-sm border-emerald-100">
              <CardHeader className="pb-3">
                <CardTitle className="text-emerald-800 text-lg flex items-center gap-2">
                  <PawPrint className="w-5 h-5" />
                  Animais Ativos ({activeAdmissions.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID IBAMA</TableHead>
                          <TableHead className="hidden sm:table-cell">Animal</TableHead>
                          <TableHead className="hidden md:table-cell">Base</TableHead>
                          <TableHead className="hidden lg:table-cell">Status</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeAdmissions
                          .slice(0, 10)
                          .map((admission) => (
                            <TableRow key={admission.id} className="hover:bg-emerald-50/50">
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Badge className={`${getGroupBadgeColor(admission.animal_group)} text-white`}>
                                    {admission.animal_group}
                                  </Badge>
                                  <div>
                                    <span className="font-medium text-emerald-800">{admission.ibama_id}</span>
                                    {admission.pmp_id && <p className="text-xs text-sky-700">PMP: {admission.pmp_id}</p>}
                                  </div>
                                </div>
                                <p className="text-sm text-gray-500 sm:hidden mt-1">{admission.common_name}</p>
                              </TableCell>
                              <TableCell className="hidden sm:table-cell">
                                <p className="font-medium">{admission.common_name}</p>
                                <p className="text-xs text-gray-500">{admission.species}</p>
                              </TableCell>
                              <TableCell className="hidden md:table-cell">
                                {admission.base_name}
                              </TableCell>
                              <TableCell className="hidden lg:table-cell">
                                <Badge variant="outline" className="capitalize">
                                  {admission.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(`/animals/${admission.id}/record`)}
                                    className="text-emerald-600 hover:bg-emerald-50"
                                    data-testid={`view-animal-${admission.id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                    <span className="hidden sm:inline ml-1">Ver</span>
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDownloadPDF(admission.id, admission.ibama_id)}
                                    className="text-gray-600 hover:bg-gray-50"
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    {activeAdmissions.length > 10 && (
                      <div className="p-4 text-center border-t">
                        <Button
                          variant="outline"
                          onClick={() => navigate('/animals')}
                          className="border-emerald-600 text-emerald-600"
                        >
                          Ver todos os animais
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar senha</DialogTitle>
            <DialogDescription>Informe a senha atual e defina a nova senha de acesso.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Senha atual</Label>
              <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div>
              <Label>Nova senha</Label>
              <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div>
              <Label>Confirmar nova senha</Label>
              <Input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPasswordDialog(false)}>Cancelar</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleChangePassword} disabled={changingPassword}>
                {changingPassword ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
